import React, { useEffect, useState } from 'react';
// Where every buy button finalizes the purchase.
export const CHECKOUT_URL = 'https://megakit-mas-clientas.impultienda.ar/checkout/megakit';
export const Button = ({
  children,
  variant = 'primary',
  className = '',
  href = CHECKOUT_URL,
  ...props
}: {
  children: React.ReactNode;
  variant?: 'primary' | 'brand';
  className?: string;
  href?: string;
}) => {
  const base = 'inline-flex items-center justify-center w-full md:w-auto text-center font-montserrat font-bold uppercase tracking-wider py-4 px-8 rounded-xl bg-payment text-white shadow-[0_8px_0_#008DBD] transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-1 active:shadow-[0_3px_0_#008DBD] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-payment focus-visible:ring-offset-2';
  const variants = {
    primary: '',
    brand: ''
  };
  const isExternal = /^https?:\/\//.test(href);
  const handleClick = () => {
    const fbq = (window as any).fbq;
    if (isExternal && typeof fbq === 'function') {
      fbq('track', 'InitiateCheckout');
    }
  };
  return <a href={href} onClick={handleClick} target={isExternal ? '_blank' : undefined} rel={isExternal ? 'noopener noreferrer' : undefined} className={`${base} ${variants[variant]} ${className}`} {...props} data-id="element-376" data-mp="34ken2438ke35">
      {children}
    </a>;
};
type TimeLeft = {
  hours: number;
  minutes: number;
  seconds: number;
};
const LAUNCH_DEADLINE_KEY = 'megakit-launch-deadline';
const LAUNCH_DURATION_MS = 12 * 60 * 60 * 1000;
function getLaunchDeadline() {
  if (typeof window === 'undefined') return Date.now() + LAUNCH_DURATION_MS;
  const savedDeadline = Number(window.localStorage.getItem(LAUNCH_DEADLINE_KEY));
  if (Number.isFinite(savedDeadline) && savedDeadline > 0) {
    return savedDeadline;
  }
  const deadline = Date.now() + LAUNCH_DURATION_MS;
  window.localStorage.setItem(LAUNCH_DEADLINE_KEY, String(deadline));
  return deadline;
}
function getTimeLeft(deadline: number): TimeLeft {
  const remainingMs = Math.max(0, deadline - Date.now());
  const totalSeconds = Math.floor(remainingMs / 1000);
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor(totalSeconds % 3600 / 60),
    seconds: totalSeconds % 60
  };
}
export const Countdown = ({
  className = ''
}: {
  className?: string;
}) => {
  const [deadline] = useState(getLaunchDeadline);
  const [timeLeft, setTimeLeft] = useState(() => getTimeLeft(deadline));
  useEffect(() => {
    const updateTimeLeft = () => setTimeLeft(getTimeLeft(deadline));
    updateTimeLeft();
    const timer = window.setInterval(updateTimeLeft, 1000);
    return () => window.clearInterval(timer);
  }, [deadline]);
  const format = (n: number) => n.toString().padStart(2, '0');
  return <div className={`font-montserrat font-bold text-4xl md:text-5xl tracking-widest ${className}`} data-id="element-377" data-mp="2uxkf880l2uiv">
      {format(timeLeft.hours)}{' '}
      <span className="text-brand text-2xl md:text-3xl" data-id="element-378" data-mp="2zosmaxefy3sv">hs</span> :{' '}
      {format(timeLeft.minutes)}{' '}
      <span className="text-brand text-2xl md:text-3xl" data-id="element-379" data-mp="o77bhg86wpaw">min</span> :{' '}
      {format(timeLeft.seconds)}{' '}
      <span className="text-brand text-2xl md:text-3xl" data-id="element-380" data-mp="j7uql10vpsay">seg</span>
    </div>;
};
export const Section = ({
  children,
  bg = 'base',
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  bg?: 'base' | 'dark' | 'mid';
  className?: string;
  id?: string;
}) => {
  const backgrounds = {
    base: 'bg-base text-dark',
    dark: 'bg-dark text-base',
    mid: 'bg-mid text-dark'
  };
  return <section id={id} className={`py-16 md:py-24 px-6 md:px-12 lg:px-24 ${backgrounds[bg]} ${className}`} data-id="element-381" data-mp="d7d5f2sqm9t">
      <div className="max-w-6xl mx-auto" data-id="element-382" data-mp="a9afom1k8mxa">{children}</div>
    </section>;
};
export const Card = ({
  children,
  className = '',
  highlighted = false
}: {
  children: React.ReactNode;
  className?: string;
  highlighted?: boolean;
}) => <div className={`bg-base text-dark rounded-xl p-6 md:p-8 shadow-sm ${highlighted ? 'border-2 border-brand shadow-md' : 'border border-gray-200'} ${className}`} data-id="element-383" data-mp="1lne6xkgrjwyn">
    {children}
  </div>;